import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { RefreshCw, Trash2, Check, X, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || 'https://smapi.sanmartino26.al'}/api`;

const STATUS_LABELS = {
  pending: "Në pritje",
  confirmed: "Konfirmuar",
  cancelled: "Anuluar",
};

const STATUS_STYLES = {
  pending: "bg-amber-50 text-amber-700 border-amber-300",
  confirmed: "bg-green-50 text-green-700 border-green-400",
  cancelled: "bg-red-50 text-red-700 border-red-300",
};

export const ReservationsView = ({ token, onUnauthorized }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  const headers = { Authorization: `Bearer ${token}` };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reservations`, { headers });
      setItems(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Sesioni skadoi.");
        onUnauthorized();
      } else {
        toast.error("Gabim në ngarkimin e të dhënave.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/reservations/${id}`, { status }, { headers });
      setItems((arr) => arr.map((it) => (it.id === id ? { ...it, status } : it)));
      toast.success("U përditësua.");
    } catch {
      toast.error("Gabim në përditësim.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Fshini këtë rezervim?")) return;
    try {
      await axios.delete(`${API}/admin/reservations/${id}`, { headers });
      setItems((arr) => arr.filter((it) => it.id !== id));
      toast.success("U fshi.");
    } catch {
      toast.error("Gabim në fshirje.");
    }
  };

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);
  const stats = {
    total: items.length,
    pending: items.filter((i) => i.status === "pending").length,
    confirmed: items.filter((i) => i.status === "confirmed").length,
    cancelled: items.filter((i) => i.status === "cancelled").length,
  };

  return (
    <div data-testid="admin-reservations-view">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-serif text-2xl text-sm-text">Rezervime</h2>
        <button
          data-testid="admin-refresh-btn"
          onClick={fetchItems}
          className="inline-flex items-center gap-2 text-[0.7rem] tracking-[0.28em] uppercase text-sm-muted hover:text-sm-olive transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Rifresko
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        {[
          { k: "all", label: "Total", v: stats.total },
          { k: "pending", label: "Në pritje", v: stats.pending },
          { k: "confirmed", label: "Konfirmuar", v: stats.confirmed },
          { k: "cancelled", label: "Anuluar", v: stats.cancelled },
        ].map((s) => (
          <button
            key={s.k}
            data-testid={`admin-filter-${s.k}`}
            onClick={() => setFilter(s.k)}
            className={`text-left border p-5 transition-colors ${filter === s.k
              ? "border-sm-olive bg-sm-surface"
              : "border-sm-border bg-sm-bg hover:border-sm-olive/50"
              }`}
          >
            <p className="text-[0.65rem] tracking-[0.28em] uppercase text-sm-muted">
              {s.label}
            </p>
            <p className="font-serif text-3xl text-sm-text mt-2">{s.v}</p>
          </button>
        ))}
      </div>

      {loading ? (
        <p data-testid="admin-loading" className="text-sm-muted py-20 text-center">
          Duke ngarkuar...
        </p>
      ) : filtered.length === 0 ? (
        <div className="border border-sm-border p-16 text-center">
          <Clock className="w-6 h-6 text-sm-muted mx-auto mb-4" />
          <p className="text-sm-muted">Asnjë rezervim për këtë filtër.</p>
        </div>
      ) : (
        <div className="border border-sm-border overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-reservations-table">
            <thead className="bg-sm-surface text-[0.65rem] tracking-[0.28em] uppercase text-sm-olive">
              <tr>
                <th className="text-left p-4">Klienti</th>
                <th className="text-left p-4">Kontakt</th>
                <th className="text-left p-4">Data</th>
                <th className="text-left p-4">Ora</th>
                <th className="text-left p-4">Persona</th>
                <th className="text-left p-4">Statusi</th>
                <th className="text-right p-4">Veprime</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`admin-row-${r.id}`}
                  className="border-t border-sm-border hover:bg-sm-surface/50"
                >
                  <td className="p-4">
                    <p className="font-medium text-sm-text">{r.full_name}</p>
                    {r.special_requests && (
                      <p className="text-xs text-sm-muted mt-1 italic max-w-xs">
                        "{r.special_requests}"
                      </p>
                    )}
                  </td>
                  <td className="p-4 text-sm-muted">
                    <a href={`tel:${r.phone}`} className="block hover:text-sm-olive">
                      {r.phone}
                    </a>
                    <a href={`mailto:${r.email}`} className="block text-xs hover:text-sm-olive">
                      {r.email}
                    </a>
                  </td>
                  <td className="p-4 font-mono text-sm-text">{r.date}</td>
                  <td className="p-4 font-mono text-sm-text">{r.time}</td>
                  <td className="p-4 text-sm-text">{r.party_size}</td>
                  <td className="p-4">
                    <span
                      className={`inline-block text-[0.65rem] tracking-[0.2em] uppercase px-2 py-1 border ${STATUS_STYLES[r.status]}`}
                    >
                      {STATUS_LABELS[r.status]}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-2">
                      {r.status !== "confirmed" && (
                        <button
                          data-testid={`admin-confirm-${r.id}`}
                          onClick={() => updateStatus(r.id, "confirmed")}
                          title="Konfirmo"
                          className="p-2 border border-green-300 text-green-700 hover:bg-green-50 transition-colors"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {r.status !== "cancelled" && (
                        <button
                          data-testid={`admin-cancel-${r.id}`}
                          onClick={() => updateStatus(r.id, "cancelled")}
                          title="Anulo"
                          className="p-2 border border-amber-300 text-amber-700 hover:bg-amber-50 transition-colors"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button
                        data-testid={`admin-delete-${r.id}`}
                        onClick={() => remove(r.id)}
                        title="Fshi"
                        className="p-2 border border-red-300 text-red-700 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ReservationsView;
